import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Lock, BookOpen } from "lucide-react";
import { catalogApi } from "@/lib/api/catalog";
import { DigitalResourceViewer } from "@/components/catalog/DigitalResourceViewer";
import { useTenant } from "@/contexts/TenantContext";

export function DigitalResourceViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenant, isLoading: tenantLoading } = useTenant();
  const [loading, setLoading] = useState(true);
  const [resourceData, setResourceData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasTriedLoading, setHasTriedLoading] = useState(false);

  useEffect(() => {
    // Wait for tenant context to be resolved (or timeout after 2 seconds)
    if (id && !hasTriedLoading) {
      if (!tenantLoading) {
        loadResource();
        setHasTriedLoading(true);
      } else {
        // Timeout fallback - try loading anyway after 2 seconds
        const timer = setTimeout(() => {
          if (!hasTriedLoading) {
            loadResource();
            setHasTriedLoading(true);
          }
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [id, tenant, tenantLoading, hasTriedLoading]);

  // Helper to get tenant slug from various sources
  const getTenantSlug = (): string | undefined => {
    // 1. From tenant context
    if (tenant?.slug) {
      return tenant.slug;
    }

    // 2. From subdomain (e.g., tenant.library.com)
    const hostname = window.location.hostname;
    if (hostname && !hostname.includes("localhost")) {
      const parts = hostname.split(".");
      if (parts.length >= 2) {
        // Assume first part is tenant slug for subdomain pattern
        return parts[0];
      }
    }

    // 3. From sessionStorage (set during login)
    const cachedSlug = sessionStorage.getItem("tenant_slug");
    if (cachedSlug) {
      return cachedSlug;
    }

    return undefined;
  };

  const loadResource = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get tenant slug for API call (needed for localhost/development)
      const tenantSlug = getTenantSlug();
      const data = await catalogApi.getPublicDigitalResource(id!, tenantSlug);

      // Check if resource is public
      if (!data.isPublic) {
        setError("This resource is not publicly available.");
        return;
      }

      setResourceData(data);
    } catch (err: any) {
      console.error("Failed to load resource:", err);
      setError(
        err.response?.data?.message || "Failed to load resource. It may not be publicly available."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading resource...</p>
        </div>
      </div>
    );
  }

  if (error || !resourceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <Lock className="h-12 w-12 text-gray-400 mx-auto" />
              <h2 className="text-xl font-semibold">Resource Not Available</h2>
              <p className="text-gray-600">{error || "Resource not found"}</p>
              <Button onClick={() => navigate("/")} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allowDownload = (resourceData.metadata as any)?.allowDownload || false;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">{resourceData.title}</h1>
                {resourceData.authors && resourceData.authors.length > 0 && (
                  <p className="text-sm text-gray-600">by {resourceData.authors.join(", ")}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{resourceData.type}</Badge>
              {allowDownload && (
                <Badge variant="default" className="bg-green-600">
                  Downloadable
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Viewer */}
      <div className="flex-1 container mx-auto px-4 py-6">
        <Card className="h-full min-h-[calc(100vh-200px)]">
          <CardContent className="p-0 h-full">
            <DigitalResourceViewer
              fileUrl={resourceData.fileUrl}
              type={resourceData.type}
              mimeType={resourceData.mimeType}
              title={resourceData.title}
              isExternalUrl={resourceData.isExternalUrl}
              onError={(err) => setError(err)}
            />
          </CardContent>
        </Card>
      </div>

      {/* Footer Info */}
      <div className="bg-white border-t py-4">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {resourceData.publisher && (
              <div>
                <Label className="text-xs font-medium text-gray-500">Publisher</Label>
                <p>{resourceData.publisher}</p>
              </div>
            )}
            {resourceData.publishedDate && (
              <div>
                <Label className="text-xs font-medium text-gray-500">Published</Label>
                <p>{resourceData.publishedDate}</p>
              </div>
            )}
            {resourceData.subjects && resourceData.subjects.length > 0 && (
              <div>
                <Label className="text-xs font-medium text-gray-500">Subjects</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {resourceData.subjects.slice(0, 3).map((subject: string, idx: number) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {subject}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
